// InspectionDetailsModal.js

import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';

const InspectionDetailsModal = ({ isOpen, onClose, inspection, onEdit, edit=true }) => {

  const [showFullTech, setShowFullTech] = useState(false);
  const [showFullInsp, setShowFullInsp] = useState(false);

  // Prepare strings safely
  const tech = inspection?.tech_notes || '';
  const insp = inspection?.inspection_notes || '';

  const techNeedsTruncate = tech.length > 200;
  const inspNeedsTruncate = insp.length > 200;

  const techVisible = techNeedsTruncate && !showFullTech ? tech.slice(0, 200) + '…' : tech;
  const inspVisible = inspNeedsTruncate && !showFullInsp ? insp.slice(0, 200) + '…' : insp;


  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, options);
  };

  const formatTimeTo12Hour = (time24) => {
    if (!time24) return '';
    const [hourStr, minuteStr] = time24.split(':');
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12; // Adjust 0 and 12 to 12
    return `${hour}:${minute} ${ampm}`;
  };
  

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95">
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
            <Dialog.Panel className="mx-auto w-full max-w-2xl overflow-hidden rounded-2xl bg-white/90 backdrop-blur-md shadow-xl ring-1 ring-gray-200">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <Dialog.Title className="text-xl font-semibold text-gray-900">
                  Inspection Details
                </Dialog.Title>

                {inspection?.status && (
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset
                      ${
                        inspection.status.toLowerCase() === 'completed'
                          ? 'bg-green-50 text-green-700 ring-green-600/20'
                          : inspection.status.toLowerCase() === 'tentative'
                          ? 'bg-red-50 text-red-700 ring-red-600/20'
                          : 'bg-indigo-50 text-indigo-700 ring-indigo-600/20'
                      }`}
                  >
                    {inspection.status}
                  </span>
                )}
              </div>

              {/* Body */}
              {inspection && (
                <div className="px-6 py-5">
                  {/* Tentative alert */}
                  {inspection.status?.toLowerCase() === 'tentative' && (
                    <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4">
                      <p className="text-sm font-semibold text-red-700">Tentative</p>
                      <p className="mt-1 text-sm text-red-700/90">
                        {inspection.status_description || 'No description provided.'}
                      </p>
                    </div>
                  )}

                  <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="rounded-lg bg-gray-50 p-4">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Inspection ID</dt>
                      <dd className="mt-1 text-sm font-medium text-gray-900">{inspection.inspection_id ?? '—'}</dd>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Customer</dt>
                      <dd className="mt-1 text-sm font-medium text-gray-900">{inspection.customer_name ?? '—'}</dd>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4 sm:col-span-2">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Job Site Address</dt>
                      <dd className="mt-1 text-sm font-medium text-gray-900">{inspection.job_site_address ?? '—'}</dd>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Lead Technician</dt>
                      <dd className="mt-1 text-sm font-medium text-gray-900">
                        {inspection.lead_tech_first_name} {inspection.lead_tech_last_name}
                      </dd>
                    </div>

                    {inspection.additional_tech_id && (
                      <div className="rounded-lg bg-gray-50 p-4">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Additional Tech</dt>
                        <dd className="mt-1 text-sm font-medium text-gray-900">
                          {inspection.additional_tech_first_name} {inspection.additional_tech_last_name}
                        </dd>
                      </div>
                    )}
                    <div className="rounded-lg bg-gray-50 p-4">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Scheduled Date
                      </dt>
                      <dd className="mt-1 text-sm font-medium text-gray-900">
                        {inspection.scheduled_due_date ?? '—'} 
                        <span className="mx-2 text-gray-400">→</span> 
                        {inspection.schedule_date_to ?? '—'}
                      </dd>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-4">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Scheduled Time</dt>
                      <dd className="mt-1 text-sm font-medium text-gray-900">
                        {inspection.schedule_time_in
                          ? formatTimeTo12Hour(inspection.schedule_time_in)
                          : 'No Time'}{' '}
                        –{' '}
                        {inspection.schedule_time_out
                          ? formatTimeTo12Hour(inspection.schedule_time_out)
                          : 'No Time'}
                      </dd>
                    </div>

                    <div className="rounded-lg bg-green-50 p-4 sm:col-span-2">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Tech Notes</dt>
                      <dd className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                        {techVisible || '—'}
                      </dd>
                      {techNeedsTruncate && (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => setShowFullTech(v => !v)}
                            className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded px-1"
                            aria-expanded={showFullTech}
                          >
                            {showFullTech ? 'Show less' : 'Show more'}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4 sm:col-span-2">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Notes</dt>
                      <dd className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                        {inspVisible || '—'}
                      </dd>
                      {inspNeedsTruncate && (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => setShowFullInsp(v => !v)}
                            className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded px-1"
                            aria-expanded={showFullInsp}
                          >
                            {showFullInsp ? 'Show less' : 'Show more'}
                          </button>
                        </div>
                      )}
                    </div>

                  </dl>
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  Close
                </button>
                {edit && (
                  <button
                    type="button"
                    onClick={onEdit}
                    className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Edit
                  </button>
                )}
              </div>
            </Dialog.Panel>
          </div>
        </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default InspectionDetailsModal;